import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { ChannelUtil } from '@commons/util/helper/channel';
import { IPromise, isString } from 'angular';

import { AgentStatus, Channel, Entity, MediaType, None, Option } from '@contact/upgrade';

const DIRECTORY_INDEX_TYPE = 'correspondent';
const STRING_MAX_LENGTH_TEXT_CORRESPONDENT: number = 60;

export interface IDirectoryService {
  getInternalCorrespondents(
    domainId: string,
    groupFilter: Option<Entity>,
    teamFilter: Option<Entity>,
    skillsFilter: Entity[],
    text?: string,
    channel?: Channel
  ): IPromise<any>;

  getInternalCorrespondentsWithTeam(
    domainId: string,
    skillsFilter: Entity[],
    text?: string,
    channel?: Channel,
    agentStatus?: AgentStatus
  ): IPromise<any>;

  getExternalCorrespondents(
    domainId: string,
    groupsFilter: Entity[],
    teamFilter: Option<Entity>,
    contactSkillNames: string[],
    text?: string,
    mediaTypes?: MediaType[]
  ): IPromise<any>;

  getCorrespondentByCsrUuid(csruuid: string, domainId: string, checkAvailabilityWithoutMediaBlending?: boolean): IPromise<any>;
}

export class DirectoryService implements IDirectoryService {
  static $inject: Array<string> = ['httpDeprecated'];

  constructor(private readonly httpDeprecated: HttpDeprecatedService) {}

  getInternalCorrespondents(
    domainId: string,
    groupFilter: Option<Entity>,
    teamFilter: Option<Entity>,
    skillsFilter: Entity[],
    text?: string,
    channel?: Channel,
    agentStatus?: AgentStatus
  ): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(this.buildInternalCorrespondentsQuery(domainId, groupFilter, teamFilter, skillsFilter, false, text, channel, agentStatus))
      .post('protected/search/searchSources', [DIRECTORY_INDEX_TYPE]);
  }

  getInternalCorrespondentsWithTeam(
    domainId: string,
    skillsFilter: Entity[],
    text?: string,
    channel?: Channel,
    agentStatus?: AgentStatus
  ): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(this.buildInternalCorrespondentsQuery(domainId, None, None, skillsFilter, true, text, channel, agentStatus))
      .post('protected/search/searchSources', [DIRECTORY_INDEX_TYPE]);
  }

  getInternalCorrespondentsConnectedWithTeam(
    domainId: string,
    skillsFilter: Entity[],
    text?: string,
    channel?: Channel,
    agentStatus?: AgentStatus
  ): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(this.buildInternalConnectedCorrespondentsQuery(domainId, None, None, skillsFilter, true, text, channel, agentStatus))
      .post('protected/search/searchSources', [DIRECTORY_INDEX_TYPE]);
  }

  getExternalCorrespondents(
    domainId: string,
    groupsFilter: Entity[],
    teamFilter: Option<Entity>,
    contactSkillNames: string[],
    text?: string,
    mediaTypes?: MediaType[]
  ): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(this.buildExternalCorrespondentsQuery(domainId, groupsFilter, teamFilter, contactSkillNames, text, mediaTypes))
      .post('protected/search/searchSources', [DIRECTORY_INDEX_TYPE]);
  }

  getCorrespondentByCsrUuid(csruuid: string, domainId: string, checkAvailabilityWithoutMediaBlending = false): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
        },
      })
      .withData(this.buildInternalCorrespondentQueryByCsrUuid(csruuid, domainId, checkAvailabilityWithoutMediaBlending))
      .post('protected/search/searchSources', [DIRECTORY_INDEX_TYPE]);
  }

  /**
   * used to put a string to lowercase and replace accented characters by their non accented version
   * @parameter inputValue the string to replace the characters in
   * @output return the inputValue string in lowercase with accented characters replaced by their non accented version
   */
  private normalize(inputValue: string): string {
    // transform a string to lowercase and replace 'àé...' to 'ae...'
    return inputValue
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private buildInternalCorrespondentQueryByCsrUuid(csruuid: string, domainId: string, checkAvailabilityWithoutMediaBlending: boolean): any {
    let query = {
      query: {
        bool: {
          filter: {
            bool: {
              must: [
                { bool: { must: [{ term: { uuid: csruuid } }] } },
                { bool: { must: [{ term: { internal: true } }] } },
                { term: { visibleForDomain: true } },
                { nested: { path: 'domains', query: { bool: { must: [{ term: { 'domains.uuid': domainId } }] } } } },
              ],
            },
          },
        },
      },
      sort: [{ 'lastName.keyword': 'asc' }, { 'firstName.keyword': 'asc' }],
    };
    query.query.bool.filter.bool.must.push({ exists: { field: 'phoneNumber' } } as any);
    query.query.bool.filter.bool.must.push({ term: { channels: 'PHONE_INCOMING' } } as any);

    if (checkAvailabilityWithoutMediaBlending) {
      query.query.bool.filter.bool.must.push({ term: { csrStatus: 'AVAILABLE' } } as any);
    }

    return query;
  }

  private buildInternalCorrespondentsQuery(
    domainId: string,
    groupFilter: Option<Entity>,
    teamFilter: Option<Entity>,
    skillsFilter: Entity[],
    withTeam: boolean,
    text?: string,
    channel?: Channel,
    agentStatus?: AgentStatus,
    withEmail: boolean = false
  ): any {
    let query: any = {};
    query.sort = [{ 'lastName.keyword': 'asc' }, { 'firstName.keyword': 'asc' }];
    query.track_total_hits = true;
    query.query = {};
    query.query.bool = {};
    query.query.bool.filter = {
      bool: {
        must: [
          { term: { internal: true } },
          { term: { visibleForDomain: true } },
          { nested: { path: 'domains', query: { bool: { must: [{ term: { 'domains.uuid': domainId } }] } } } },
        ],
      },
    };
    if (agentStatus) {
      query.query.bool.filter.bool.must.push({
        term: { csrStatus: agentStatus },
      });
    }
    if (withEmail) {
      query.query.bool.filter.bool.must.push({ exists: { field: 'email' } } as any);
      query.query.bool.filter.bool.must.push({ term: { channels: 'MAIL_INCOMING' } } as any);
    } else if (!!channel && ChannelUtil.toString(channel).indexOf('PHONE') < 0) {
      query.query.bool.filter.bool.must.push({ term: { channels: ChannelUtil.toString(channel) } } as any);
    } else {
      query.query.bool.filter.bool.must.push({ exists: { field: 'phoneNumber' } } as any);
      query.query.bool.filter.bool.must.push({ term: { csrStatus: 'AVAILABLE' } } as any);
      query.query.bool.filter.bool.must.push({ term: { channels: 'PHONE_INCOMING' } } as any);
    }
    if (isString(text) && text.length > 0) {
      query.query.bool.should = [
        { wildcard: { 'firstName.keyword': '*' + this.normalize(text) + '*' } },
        { wildcard: { 'lastName.keyword': '*' + this.normalize(text) + '*' } },
        { wildcard: { 'name.keyword': '*' + this.normalize(text) + '*' } },
        { prefix: { email: text } },
        { term: { 'directory.name.keyword': text } },
        { prefix: { phoneNumber: text } },
        { term: { 'category.name.keyword': text } },
      ];
      query.query.bool.minimum_should_match = 1;
    } else if (withTeam && teamFilter === None) {
      query.size = 1000;
    } else if (!!channel && ChannelUtil.toString(channel).indexOf('CHAT') > -1) {
      query.size = 200;
    } else {
      query.size = 50;
    }

    if (withTeam && teamFilter === None) {
      let teamQuery = { nested: { path: 'teams', query: { bool: { must: [{ exists: { field: 'teams.uuid' } }] } } } };
      (query.query.bool.filter.bool.must as any[]).push(teamQuery);
    } else {
      groupFilter.forEach(group => {
        let groupQuery = { nested: { path: 'groups', query: { bool: { must: [{ terms: { 'groups.uuid': [group.id] } }] } } } };
        (query.query.bool.filter.bool.must as any[]).push(groupQuery);
      });
      teamFilter.forEach(team => {
        let teamQuery = { nested: { path: 'teams', query: { bool: { must: [{ term: { 'teams.uuid': team.id } }] } } } };
        (query.query.bool.filter.bool.must as any[]).push(teamQuery);
      });
    }

    if (skillsFilter.length > 0) {
      let skillIds = skillsFilter.map(skill => skill.id);
      // if no search with text search for all skills
      query.query.bool.must = [];
      skillIds.forEach(currentSkillId => {
        query.query.bool.must.push({
          terms: { 'skills.uuid': [currentSkillId] },
        });
      });
    }

    return query;
  }

  private buildInternalConnectedCorrespondentsQuery(
    domainId: string,
    groupFilter: Option<Entity>,
    teamFilter: Option<Entity>,
    skillsFilter: Entity[],
    withTeam: boolean,
    text?: string,
    channel?: Channel,
    agentStatus?: AgentStatus,
    withEmail: boolean = false
  ): any {
    let query = this.buildInternalCorrespondentsQuery(
      domainId,
      groupFilter,
      teamFilter,
      skillsFilter,
      withTeam,
      text,
      channel,
      agentStatus,
      withEmail
    );
    query.query.bool.filter.bool.must.push({
      term: { isConnected: true },
    });
    return query;
  }

  private buildExternalCorrespondentsQuery(
    domainId: string,
    groupsFilter: Entity[],
    teamFilter: Option<Entity>,
    contactSkillNames: string[],
    text?: string,
    mediaTypes?: MediaType[]
  ): any {
    let query: any = {};
    query.sort = [{ 'lastName.keyword': 'asc' }, { 'firstName.keyword': 'asc' }];
    query.track_total_hits = true;
    const withPhone = !mediaTypes || mediaTypes.length === 0 || mediaTypes.some(m => m === MediaType.PHONE);
    const withMail = mediaTypes && mediaTypes.some(m => m === MediaType.MAIL);
    query.query = {
      bool: {
        must: [
          { term: { internal: false } },
          {
            bool: {
              should: [
                {
                  bool: {
                    must: [
                      { term: { visibleForDomain: true } },
                      { nested: { path: 'domains', query: { bool: { must: [{ term: { 'domains.uuid': domainId } }] } } } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    };

    // for JIRA ODECSCNGACCEPTANCE-11120 test non empty field
    if (withPhone) {
      query.query.bool.must.unshift({ exists: { field: 'phoneNumber' } });

      let nonEmptyKeywordField: string = 'phoneNumber';
      let nonEmptyFieldQuery: any = {
        bool: {
          must_not: {
            term: {},
          },
        },
      };
      nonEmptyFieldQuery.bool.must_not.term[nonEmptyKeywordField] = '';
      query.query.bool.must.push(nonEmptyFieldQuery);
    }

    if (withMail) {
      query.query.bool.must.unshift({ exists: { field: 'email' } });

      let nonEmptyKeywordField: string = 'email';
      let nonEmptyFieldQuery: any = {
        bool: {
          must_not: {
            term: {},
          },
        },
      };
      nonEmptyFieldQuery.bool.must_not.term[nonEmptyKeywordField] = '';
      query.query.bool.must.push(nonEmptyFieldQuery);
    }

    if (isString(text) && text.length > 0) {
      const normalizedText = this.normalize(text).slice(0, STRING_MAX_LENGTH_TEXT_CORRESPONDENT);
      const splitted = normalizedText.split(' ');

      query.query.bool.should = [];

      if (splitted.length > 1) {
        splitted.map(sub => {
          if (sub.length >= 3) {
            query.query.bool.should.push({
              wildcard: {
                'firstName.keyword': {
                  value: `*${sub}*`,
                },
              },
            });
            query.query.bool.should.push({
              wildcard: {
                'lastName.keyword': {
                  value: `*${sub}*`,
                },
              },
            });
          }
        });
      }

      query.query.bool.should = query.query.bool.should.concat([
        {
          wildcard: {
            'firstName.keyword': {
              value: `*${normalizedText}*`,
            },
          },
        },
        {
          wildcard: {
            'lastName.keyword': {
              value: `*${normalizedText}*`,
            },
          },
        },
        {
          wildcard: {
            'name.keyword': {
              value: `*${normalizedText.replace(' ', '.')}*`,
            },
          },
        },
        { term: { 'directory.name.keyword': text.toLowerCase() } },
        { term: { 'category.name.keyword': text.toLowerCase() } },
      ]);

      if (withPhone) {
        query.query.bool.should.push({ prefix: { phoneNumber: text } });
        query.query.bool.should.push({ prefix: { shortNumber: text } });
      }
      if (withMail) {
        query.query.bool.should.push({ prefix: { email: text.toLowerCase() } });
      }

      query.query.bool.minimum_should_match = 1;
      // ODECSCNGACCEPTANCE-14524 always set a size so the default 10 does not apply in BL
      query.size = 100;
    } else {
      query.size = 50;
    }

    if (contactSkillNames.length > 0) {
      let skillFilter = {
        bool: {
          should: [
            {
              bool: {
                must_not: {
                  exists: {
                    field: 'skills.name',
                  },
                },
              },
            },
            { bool: { must: new Array() } },
          ],
        },
      };
      contactSkillNames.forEach(skillName => {
        (skillFilter.bool.should[1].bool as any).must.push({ term: { 'skills.name.keyword': skillName } });
      });
      (query.query.bool.must as any[]).push(skillFilter);
    }

    if (groupsFilter.length > 0) {
      let groupIds = groupsFilter.map(group => group.id);
      (query.query.bool.must[2] as any).bool.should.push({
        bool: {
          // domain visiblity false and group uuid
          must: [
            { term: { visibleForDomain: false } },
            { nested: { path: 'groups', query: { bool: { must: [{ terms: { 'groups.uuid': groupIds } }] } } } },
          ],
        },
      });
    }

    teamFilter.forEach(team => {
      (query.query.bool.must[2] as any).bool.should.push({
        bool: {
          // domain visiblity false and team uuid
          must: [
            { term: { visibleForDomain: false } },
            { nested: { path: 'teams', query: { bool: { must: [{ term: { 'teams.uuid': team.id } }] } } } },
          ],
        },
      });
    });

    return query;
  }
}

export default 'directory';
